import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import { SurveyDataRequested } from "../../../store/Models/Surveys/SurveyDataModel";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../ToxnotReactModal";
import { ChangeEvent, useState } from "react";
import { ProgressBar } from "../ProgressBar";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { ProcessingStage } from "../../../store/Models/SharedMaterials/DataModel";
import { OfflineProcessStageManager } from "../../../utilities/OfflineProcessStage";

interface Props {
    localizer: IStringLocalizer;
    responseId: string;
    materialIdsDataToLoadList?: string[];
    requestedData?: SurveyDataRequested;
    bomQuestionnaireId?: string;
    needsMaterialBomResponse: boolean;
}

const BatchButtons: React.FC<Props> = ({ localizer, responseId, materialIdsDataToLoadList, requestedData, bomQuestionnaireId, needsMaterialBomResponse }) => {
    const [formVisibility, setFormVisibility] = useState(true);
    const [uploadVisibility, setUploadVisibility] = useState(false);
    const [importVisibility, setImportVisibility] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [doneVisibility, setDoneVisibility] = useState(false);
    const [wasSuccessful, setWasSuccessful] = useState(false);
    const [warningVisiblity, setWarningVisiblity] = useState(false);
    const [totalNumItems, setTotalNumItems] = useState(0);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [importStatusMessage, setImportStatusMessage] = useState(OfflineProcessStageManager.processingStageMessages.first(s => s.processingStage == ProcessingStage.Queued).friendlyMessage);
    const modalId = "bulk-import-boms-modal";
    const ok = "Ok";
    const trueAsString = "true";

    const serviceClient = new ToxnotServiceClient();
    let file: File | null;

    const createRedirectButton = (buttonText: string, redirectUrl: string) => {
        return <button className="btn btn-default m-r-5 import-button" onClick={() => window.open(redirectUrl, "_blank")}> {buttonText}</button >
    }

    const isAskingForAnySummaryData = () => {
        let dataRequested = requestedData ?? null;
        if (dataRequested !== null)
            if (dataRequested.circularityDataAsked ||
                dataRequested.complianceList.length > 0 ||
                dataRequested.sustainabilityDataAsked ||
                dataRequested.documentsCertificationList.length)
                return true;
        return false;
    }

    const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files?.length > 0) {
            file = e.target.files[0];
        } else {
            file = null;
        }
    }

    const onSubmit = () => {
        if (file === null || file === undefined) {
            setWarningVisiblity(true);
        } else {
            setWarningVisiblity(false);
            setFormVisibility(false);
            setUploadVisibility(true);
            serviceClient.batchSurveyResponseBomImport(responseId, file).then((result) => {
                setUploadVisibility(false);

                if (result?.result == ok) {
                    setUploadProgress(1);
                    setImportVisibility(true);
                    serviceClient.enqueueImport(result.importId).then(() => {
                        var getDataInterval = setInterval(() => { getImportData(result.importId, getDataInterval) }, 1500);
                    })
                }
            })
        }
    }

    const getImportData = (importId: string, getDataInterval: any) => {
        serviceClient.importDataGet(importId, trueAsString).then((result) => {
            console.log(result)
            setTotalNumItems(result?.import.state.progressTotalNumItems ?? 0);
            setCurrentProgress(result?.import.state.currentProgressItemIndex ?? 0);
            setImportStatusMessage(OfflineProcessStageManager.processingStageMessages.firstOrDefault(s => s.processingStage == result?.import.state.processingStage)?.friendlyMessage ?? "");

            if (result?.import.state.processingStage == ProcessingStage.TotallyComplete) {
                setWasSuccessful(true);
                clearInterval(getDataInterval)
                setDoneVisibility(true);
                setImportVisibility(false);
            } else if (result?.import.state.processingStage == ProcessingStage.Failed) {
                setWasSuccessful(false);
                clearInterval(getDataInterval)
                setDoneVisibility(true);
                setImportVisibility(false);
            }
        });
    }

    $("#" + modalId).off("hidden.bs.modal").on("hidden.bs.modal", () => {
        setUploadProgress(0);
        setCurrentProgress(0);
        setTotalNumItems(0);
        setDoneVisibility(false);
        setFormVisibility(true);
        setUploadVisibility(false);
        setImportVisibility(false);
        setWarningVisiblity(false);
        setImportStatusMessage(OfflineProcessStageManager.processingStageMessages.first(s => s.processingStage == ProcessingStage.Queued).friendlyMessage);
    })

    const bulkImportSds = needsMaterialBomResponse && (materialIdsDataToLoadList && materialIdsDataToLoadList?.length >= SurveyResponseController.materiaListSizeGroupThreshold)
        ? createRedirectButton(localizer.localizeString("Bulk Import SDS Files"), encodeURI(`/Data/NewProduct?responseId=${responseId}&mode=MSDS Library`))
        : null;

    const batchAnswerQuestionnaire = createRedirectButton(localizer.localizeString("Batch Answer Questionnaire"),
        encodeURI((isAskingForAnySummaryData()) ?
            `/Surveys/AnswerQuestionnaire?responseId=${responseId}&mode=batch&questionnaireType=InventorySummaryData&return=true`
            : `/Surveys/AnswerQuestionnaire?responseId=${responseId}&mode=batch&questionnaireType=Default&return=true`))

    const bulkImportSpreadsheet = needsMaterialBomResponse && <button className="btn btn-default m-r-5 import-button" data-toggle="modal" data-target="#bulk-import-boms-modal">{localizer.localizeString("Bulk Import Spreadsheet")}</button>

    return (
        <>
            <div className="m-b-10 page-title-border">
                <h4 className="m-t-10 m-b-10">{localizer.localizeString("Bulk Inputs")}
                    <span className="badge m-l-5">{localizer.localizeString("Optional")}</span>
                </h4>
                <p>
                    {localizer.localizeString("To start the process you can bulk import the substances for your products and 3E Exchange will help to screen for regulated substances to respond to customer surveys.")}
                    <b><i>{localizer.localizeString("Import substances via spreadsheet or Safety Data Sheet below")}</i></b>
                </p>
            </div>
            <div className="batch-import-container">
                {bulkImportSds}
                {bulkImportSpreadsheet}
                {batchAnswerQuestionnaire}
            </div>
            <ToxnotReactModal modalId={modalId} localizedTitle={localizer.localizeString("Bulk Import Material Content")}>
                <ToxnotReactModalBody>
                    {formVisibility &&
                        <div className="upload-form">
                            <p>
                                {localizer.localizeString("You can import data from a Spreadsheet File (Excel XLSX, or CSV).")}
                            </p>
                            <p>
                                <a href={`/Api/ImportTemplateForResponse?responseId=${responseId}&generateBatchImportTemplate=True&exportOnlyInventoriesWithRevisionRequests=True`}>{localizer.localizeString("Click here to download a bulk import template for many materials.")}</a>
                            </p>
                            <div className="alert alert-warning">
                                {localizer.localizeString("Warning: When you import data from a file, you'll overwrite all existing material data in the editor.")}
                            </div>
                            <div className="form-group">
                                <input type="file" id="bulk-import-boms-file-input" className="form-control" accept=".csv,.txt,.tsv,.xlsx,.xlsm" onChange={(e) => onFileChange(e)} />
                            </div>
                        </div>
                    }
                    {uploadVisibility &&
                        <div className="spreadsheet-upload-progress-form">
                            <div id="file-upload-status-container">
                                <ProgressBar localizedTitle={localizer.localizeString("Uploading File")} totalNumItems={1} currentProgressIndex={uploadProgress} localizer={localizer}></ProgressBar>
                            </div>
                        </div>
                    }
                    {importVisibility &&
                        <div className="offline-import-status-form">
                            <div id="offline-import-status-container">
                                <div className="alert alert-info">
                                    <p>{localizer.localizeString("Toxnot is importing your data. This might take a while.")}</p>
                                    <p>{localizer.localizeString("We'll email you when it's done, or you can come back later and check your status on the Imports page.")}</p>
                                    <p>{localizer.localizeString("You can safely leave this page.")}</p>
                                </div>
                                <ProgressBar localizedTitle={localizer.localizeString(importStatusMessage)} totalNumItems={totalNumItems} currentProgressIndex={currentProgress} localizer={localizer}></ProgressBar>
                            </div>
                        </div>
                    }
                    {doneVisibility &&
                        <div className="done-form">
                            {wasSuccessful == true ?
                                <div className="alert alert-success">
                                    {localizer.localizeString("Your import was successful.")}
                                </div> :
                                <div className="alert alert-danger">
                                    {localizer.localizeString("Sorry, there was a problem accessing the service. Please try again.")}
                                </div>}
                        </div>
                    }
                    {warningVisiblity &&
                        <div className="alert alert-danger">
                            <p>
                                {localizer.localizeString("Please select a file for upload.")}
                            </p>
                        </div>
                    }
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} enabledCloseButton={formVisibility}>
                    {formVisibility && <button type="button" id="bulk-import-boms-submit-button" className="btn btn-toxnot-primary" onClick={() => onSubmit()}>{localizer.localizeString("Import File")} </button>}
                    {doneVisibility && <button type="button" data-dismiss="modal" id="bulk-import-boms-done-button" className="btn btn-toxnot-primary" onClick={() => wasSuccessful ? null : window.location.reload()}>{localizer.localizeString("Ok")}</button>}
                </ToxnotReactModalFooter>
            </ToxnotReactModal>
        </>
    )
}

export default BatchButtons;